/**
 * Builds an 8-panel character reference sheet prompt for image generation.
 *
 * The 8 panels (always the same):
 *   FULL-BODY TURNAROUND:
 *     P1: Full body FRONT     P2: Full body 45°
 *     P3: Full body SIDE      P4: Full body BACK
 *   FACIAL IDENTITY CLOSE-UPS:
 *     P5: Extreme close-up FRONT (the identity lock — pores, skin texture, asymmetry)
 *     P6: Close-up 45° LEFT
 *     P7: Close-up 45° RIGHT
 *     P8: SIGNATURE EXPRESSION
 *
 * Layout adapts to the project's aspect ratio so figures and faces stay legible:
 *
 *   LANDSCAPE (16:9, 4:3, anything wider than tall)
 *     ┌────┬────┬────┬────┬──┬──┐
 *     │ P1 │ P2 │ P3 │ P4 │P5│P6│   Left ⅔: 4 tall full-bodies in one row
 *     │    │    │    │    ├──┼──┤   Right ⅓: 2×2 close-up grid
 *     │    │    │    │    │P7│P8│
 *     └────┴────┴────┴────┴──┴──┘
 *
 *   SQUARE (1:1)
 *     ┌────┬────┬────┬────┐
 *     │ P1 │ P2 │ P3 │ P4 │   Top ~65%: row of 4 full-bodies
 *     │    │    │    │    │
 *     ├────┼────┼────┼────┤
 *     │ P5 │ P6 │ P7 │ P8 │   Bottom ~35%: row of 4 close-ups
 *     └────┴────┴────┴────┘
 *
 *   PORTRAIT (9:16, 3:4, anything taller than wide)
 *     ┌────────┬────────┐
 *     │   P1   │   P2   │
 *     ├────────┼────────┤   Top half: 2×2 full-body grid
 *     │   P3   │   P4   │
 *     ├────────┼────────┤
 *     │   P5   │   P6   │
 *     ├────────┼────────┤   Bottom half: 2×2 close-up grid
 *     │   P7   │   P8   │
 *     └────────┴────────┘
 *
 * @param {string} name        - Character display name
 * @param {string} description - Free-form character description from the script
 * @param {string} aspectRatio - 'W:H' string (e.g. '16:9', '1:1', '9:16'). Defaults to 16:9.
 * @param {Object} [options]
 * @param {boolean} [options.tryOnCostume=false] - When true, append instructions
 *   telling the generator to dress the character in EXACTLY the costume shown
 *   in the costume reference images across ALL 8 panels. Used by the "Try On"
 *   action so the entire character sheet is regenerated with the new outfit.
 * @returns {string}             Prompt ready for the image generator
 */
export const buildCharacterSheetPrompt = (name, description, aspectRatio = '16:9', options = {}) => {
    const safeName = (name || '').trim() || 'the character';
    const safeDesc = (description || '').trim();
    const briefLine = safeDesc ? `${safeName} — ${safeDesc}` : safeName;
    const { tryOnCostume = false } = options;

    // Classify the aspect ratio into one of three layout buckets.
    const orientation = classifyOrientation(aspectRatio);

    const layoutBlock = LAYOUT_BLOCKS[orientation];

    return [
        // === Header / identity ===
        `Professional 8-panel character reference sheet of ${briefLine}.`,
        `This is a single cohesive ${orientation.toUpperCase()} image (NOT eight separate images) used by animators and storyboard artists as the definitive identity reference for the character.`,
        `Canvas aspect ratio: ${aspectRatio}.`,

        // === Layout-specific block ===
        layoutBlock,

        // === Panel content (same 8 panels regardless of layout) ===
        `THE 4 FULL-BODY PANELS (P1-P4) — turnaround, identical lighting and pose energy across all four, feet on a shared ground line:`,
        `PANEL 1 (full body FRONT): neutral relaxed pose, arms by sides, looking straight at camera. Entire outfit visible head-to-toe. Purpose: lock body proportions, costume, height, build.`,
        `PANEL 2 (full body 45° three-quarter view): same neutral pose, same outfit, slightly rotated. Purpose: face and body depth.`,
        `PANEL 3 (full body SIDE PROFILE): complete profile, perpendicular to camera. Purpose: nose shape, chin projection, silhouette consistency.`,
        `PANEL 4 (full body BACK view): hair from behind, rear of clothing, body structure. Purpose: prevent invented back details later.`,

        `THE 4 CLOSE-UP PANELS (P5-P8) — framed top of hair to just below chin, sharp focus on facial detail, faces nearly fill each panel:`,
        `PANEL 5 (EXTREME CLOSE-UP FRONT portrait): the most important panel. Show pores, skin texture, lip texture, eye detail, beauty marks, natural facial asymmetry. Purpose: identity lock.`,
        `PANEL 6 (close-up 45° LEFT portrait): head turned to character's left. Reveal nose shape, cheekbone structure, jawline.`,
        `PANEL 7 (close-up 45° RIGHT portrait): opposite side from Panel 6. Reveal opposite-side facial details, ear shape, hairline.`,
        `PANEL 8 (SIGNATURE EXPRESSION portrait): choose a single emotion that best fits ${safeName} — from warm smile, confident smirk, serious detective look, hero expression, or natural neutral.`,

        // === Hard consistency rules ===
        `CRITICAL CONSISTENCY: every one of the 8 panels MUST depict the SAME person — identical face, identical hair colour and style, identical body type, identical eye colour, identical skin tone, identical outfit, identical accessories. Treat this like a turnaround / model sheet used by a professional animation studio.`,
        `DO NOT duplicate a pose. DO NOT add extra full-body panels. DO NOT shrink the full-body figures — give them maximum vertical room within their cells. Close-ups must have faces filling the cells.`,

        // === Costume try-on override (only when called from the "Try On" action) ===
        tryOnCostume
            ? `COSTUME TRY-ON OVERRIDE: the character MUST wear EXACTLY the outfit shown in the COSTUME REFERENCE IMAGES provided alongside this prompt — not the outfit described in the text. Replicate the exact garment silhouette, fabric, colour, pattern, accessories and footwear visible in the costume references across ALL 8 panels. The face, hair, body type, eye colour and skin tone come from the CHARACTER REFERENCE IMAGE (also provided) — keep those locked. Only the OUTFIT is being changed. Both the four full-body panels (P1-P4) and the four close-up panels (P5-P8) must show the same character now wearing the new costume. In close-up panels, where the body is cropped, show whatever portion of the new garment is visible at the collar / shoulders / neckline so the costume change is consistent.`
            : '',

        // === Style ===
        `STYLE: cinematic photoreal, sharp focus, soft even studio key light, no harsh shadows, flat off-white or pale neutral grey backdrop, no props, no text labels, no panel numbers, no captions, no watermarks, no signatures, no border frame around the whole sheet. Use thin clean dividers between panels.`,
        `OUTPUT: one single high-resolution cohesive reference sheet image with exactly 8 panels arranged in the layout described above.`,
    ].filter(Boolean).join(' ');
};

// ────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * Classify a 'W:H' aspect ratio string into one of three orientation buckets.
 * Falls back to 'landscape' for malformed input.
 */
const classifyOrientation = (aspectRatio) => {
    const m = String(aspectRatio || '').match(/^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/);
    if (!m) return 'landscape';
    const w = parseFloat(m[1]);
    const h = parseFloat(m[2]);
    if (!w || !h) return 'landscape';
    const ratio = w / h;
    // Tolerance around 1.0 so 4:5, 5:4 etc. still pick a clean layout.
    if (ratio >= 1.15) return 'landscape';
    if (ratio <= 0.85) return 'portrait';
    return 'square';
};

const LAYOUT_BLOCKS = {
    landscape: [
        `LAYOUT — single LANDSCAPE image divided into TWO horizontal regions on a flat neutral off-white backdrop:`,
        `• LEFT REGION (~⅔ of canvas width): a single row of exactly 4 equal-width TALL full-body panels side-by-side. Each full-body panel spans the FULL height of the canvas, head-to-toe, with feet on a shared ground line. Figures must be TALL and clearly legible.`,
        `• RIGHT REGION (~⅓ of canvas width): a 2×2 grid of 4 close-up portrait panels — 2 columns wide and 2 rows tall. Each close-up panel spans half the canvas height.`,
        `TOTAL = 4 tall full-body panels (left) + 4 close-up panels (right 2×2) = 8 panels in one image.`,
        `PANEL ORDER:`,
        `• Left region, columns 1-4 (left to right): P1 Front, P2 45°, P3 Side, P4 Back.`,
        `• Right region 2×2 (reading order): P5 top-left, P6 top-right, P7 bottom-left, P8 bottom-right.`,
    ].join(' '),

    square: [
        `LAYOUT — single SQUARE image divided into TWO horizontal STRIPS on a flat neutral off-white backdrop:`,
        `• TOP STRIP (~65% of canvas height): a single row of exactly 4 equal-width full-body panels side-by-side. Each cell is portrait-oriented; figures stand head-to-toe with feet on a shared ground line and fill the cell vertically.`,
        `• BOTTOM STRIP (~35% of canvas height): a single row of exactly 4 equal-width close-up portrait panels side-by-side. Each cell is roughly landscape-oriented and the face fills the cell.`,
        `TOTAL = 4 full-body panels (top) + 4 close-up panels (bottom) = 8 panels in one image.`,
        `PANEL ORDER:`,
        `• Top row, columns 1-4 (left to right): P1 Front, P2 45°, P3 Side, P4 Back.`,
        `• Bottom row, columns 1-4 (left to right): P5 Extreme front close-up, P6 45° Left close-up, P7 45° Right close-up, P8 Signature expression.`,
    ].join(' '),

    portrait: [
        `LAYOUT — single PORTRAIT (vertical) image divided into TWO horizontal regions on a flat neutral off-white backdrop:`,
        `• TOP HALF (~50% of canvas height): a 2×2 grid of 4 full-body panels — 2 columns wide and 2 rows tall. Each full-body cell occupies ~half the canvas width and ~25% of canvas height. Figures stand head-to-toe with feet on the panel's bottom edge; give each figure maximum vertical room within its cell.`,
        `• BOTTOM HALF (~50% of canvas height): a 2×2 grid of 4 close-up portrait panels — 2 columns wide and 2 rows tall. Each close-up cell occupies ~half the canvas width and ~25% of canvas height; the face fills the cell.`,
        `TOTAL = 4 full-body panels (top 2×2) + 4 close-up panels (bottom 2×2) = 8 panels in one image.`,
        `PANEL ORDER:`,
        `• Top 2×2 (reading order): P1 top-left Front, P2 top-right 45°, P3 bottom-left Side, P4 bottom-right Back.`,
        `• Bottom 2×2 (reading order): P5 top-left Extreme front close-up, P6 top-right 45° Left close-up, P7 bottom-left 45° Right close-up, P8 bottom-right Signature expression.`,
    ].join(' '),
};

export default buildCharacterSheetPrompt;
